import { ConditionsNew, PackageType } from '@retail/calculus/types';
import { HierarchyLevels } from '@retail/products/types';
import { getConditionBindingId } from './groupConditionsByBinding';

export const SUPPLIER_LEVEL_BINDING: HierarchyLevels = 'MG_PARTICIPANT_ACCOUNT';

const PACKAGE_TYPE_ORDER: PackageType[] = ['PRIMARY', 'SECONDARY', 'TERTIARY'];

const packageRank = (condition: ConditionsNew): number => {
  const index = condition.bindingTargetItem?.packageType
    ? PACKAGE_TYPE_ORDER.indexOf(condition.bindingTargetItem.packageType)
    : 0;
  return index === -1 ? 0 : index;
};

const supplierSegment = (condition: ConditionsNew): string | undefined =>
  condition.supplier?.supplierNumber != null ? `s:${condition.supplier.supplierNumber}` : undefined;

// The product-group condition whose bindingTargetId is the longest prefix of `targetId`
// (excluding an exact match), i.e. the closest ancestor in the code hierarchy.
const closestProductGroupAncestor = (
  targetId: string,
  candidates: ConditionsNew[]
): ConditionsNew | undefined =>
  candidates
    .filter((c) => c.bindingTargetId !== targetId && targetId.startsWith(c.bindingTargetId))
    .sort((a, b) => b.bindingTargetId.length - a.bindingTargetId.length)[0];

/**
 * Builds a tree-data path (keyed by binding id) for each condition:
 *  - supplier-level conditions become the supplier node itself
 *  - OVERGRUPPE/HOVEDGRUPPE/VAREGRUPPE nest by their bindingTargetId prefix within a
 *    supplier: a varegruppe sits under its hovedgruppe (or overgruppe, or the supplier
 *    directly when neither exists), and a hovedgruppe under its overgruppe or the supplier
 *  - without a supplier, the assortment condition is the root and the other conditions
 *    (product groups) nest under it when it exists
 *  - MG_SUPPLIER_ITEM conditions sharing an mg item (bindingTargetId) nest by package type
 *    (PRIMARY > SECONDARY > TERTIARY), each under the closest higher-ranked one that exists
 *  - every other binding type stays a direct child of the supplier, as before
 */
export const buildConditionTreePaths = (
  conditions: ConditionsNew[],
  getId: (condition: ConditionsNew) => string = getConditionBindingId
): Map<string, string[]> => {
  const bySupplier = new Map<string | undefined, ConditionsNew[]>();
  conditions.forEach((condition) => {
    const key = supplierSegment(condition);
    const list = bySupplier.get(key) ?? [];
    list.push(condition);
    bySupplier.set(key, list);
  });

  const paths = new Map<string, string[]>();

  bySupplier.forEach((supplierConditions, supplier) => {
    const overgrupper = supplierConditions.filter((c) => c.bindingType === 'OVERGRUPPE');
    const hovedgrupper = supplierConditions.filter((c) => c.bindingType === 'HOVEDGRUPPE');
    const assortmentCondition = supplier
      ? undefined
      : supplierConditions.find((c) => c.bindingType === 'ASSORTMENT');
    const prefix = supplier
      ? [supplier]
      : assortmentCondition
      ? [getId(assortmentCondition)]
      : [];

    supplierConditions.forEach((condition) => {
      const id = getId(condition);

      if (condition.bindingType === SUPPLIER_LEVEL_BINDING && supplier) {
        paths.set(id, [supplier]);
        return;
      }

      if (condition.bindingType === 'ASSORTMENT' && !supplier) {
        paths.set(id, [id]);
        return;
      }

      const ancestors: ConditionsNew[] = [];
      if (condition.bindingType === 'HOVEDGRUPPE') {
        const over = closestProductGroupAncestor(condition.bindingTargetId, overgrupper);
        if (over) ancestors.push(over);
      } else if (condition.bindingType === 'VAREGRUPPE') {
        const hoved = closestProductGroupAncestor(condition.bindingTargetId, hovedgrupper);
        const over = closestProductGroupAncestor(
          hoved?.bindingTargetId ?? condition.bindingTargetId,
          overgrupper
        );
        if (over) ancestors.push(over);
        if (hoved) ancestors.push(hoved);
      } else if (condition.bindingType === 'MG_SUPPLIER_ITEM') {
        const rank = packageRank(condition);
        const packageAncestors = supplierConditions
          .filter(
            (c) =>
              c.bindingType === 'MG_SUPPLIER_ITEM' &&
              c.bindingTargetId === condition.bindingTargetId &&
              packageRank(c) < rank
          )
          .sort((a, b) => packageRank(a) - packageRank(b));
        ancestors.push(...packageAncestors);
      }

      paths.set(id, [...prefix, ...ancestors.map(getId), id]);
    });
  });

  return paths;
};
